// VolumeController.jsx
import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { throttle } from 'lodash';

import Seekbar from '../component/Seekbar.jsx';
import Tooltip from '../component/Tooltip.jsx';
import LivestreamUnmuteHint, {
  trackUnmuteHintOpened,
  getIsUnmuteHintVisibleLayout,
} from '../component/LivestreamUnmuteHint.jsx';

import { ButtonId } from '../resource/trackingConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';

import AudioOnIconSrc from '../../img/ic-audio-on.svg';
import AudioOffIconSrc from '../../img/ic-audio-off.svg';

import { color } from '../style/variables.js';

const VOLUME_THROTTLE_MSEC = 25;

export const VolumeController = ({
  volume = 1,
  isMuted = true,
  isRenderSlider = false,
  isDragToMute = false,
  volumeBeforeDragToMute = null,
  isStreamActivated = false,
  isCoveredByEntryGate = false,
  isUnmuteHintEnabled = false,
  shouldDisplayUnmuteHint = false,
  unmuteHintDurationSeconds,
  setPlayerData = NULL_FUNCTION,
  markUnmuteHintSeen = NULL_FUNCTION,
}) => {
  // Throttle setPlayerData to reduce Redux dispatch frequency during drag
  const setPlayerDataThrottled = useMemo(
    () =>
      throttle(setPlayerData, VOLUME_THROTTLE_MSEC, {
        leading: true,
        trailing: true,
      }),
    [setPlayerData]
  );

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => setPlayerDataThrottled.cancel();
  }, [setPlayerDataThrottled]);

  // First visit on this device && the unmute hint is enabled && the stream is
  // still muted && the desktop footer is the visible one -> auto-open once.
  // The one-shot must not be spent while the viewer cannot see the button:
  // before the stream data lands, every other flag reads as "nothing here".
  // Entry gates (landing page, age verifier) are derived from remote config, so
  // they read as covering before their modal reaches the store. Sales popups and
  // the login flow are not covered -- no config predicts them.
  const shouldTriggerUnmuteHint =
    isStreamActivated &&
    !isCoveredByEntryGate &&
    isUnmuteHintEnabled &&
    shouldDisplayUnmuteHint &&
    isMuted &&
    getIsUnmuteHintVisibleLayout();

  const handleUnmuteHintOpened = useCallback(() => {
    trackUnmuteHintOpened();
    // Mark seen as soon as it opens, so it only shows once and the mobile
    // footer does not display it as well. This also disables the Tooltip,
    // which stops hover from reopening it.
    markUnmuteHintSeen();
  }, [markUnmuteHintSeen]);

  const renderUnmuteHint = useCallback(() => <LivestreamUnmuteHint />, []);

  const handleMuteOnClick = useCallback(() => {
    if (isMuted) {
      setPlayerData({ isMuted: false, isFirstUnmuted: false });
      if (isDragToMute) {
        setPlayerData({ volume: volumeBeforeDragToMute, isDragToMute: false });
      }
    } else {
      setPlayerData({ isMuted: true, isFirstUnmuted: false });
    }
  }, [isMuted, setPlayerData, isDragToMute, volumeBeforeDragToMute]);

  // record volume before drag to 0 (mute)
  const handleSeekStart = useCallback(() => {
    setPlayerData({ volumeBeforeDragToMute: volume });
  }, [volume, setPlayerData]);

  const handleVolumeChange = useCallback(
    ({ positionPercentage }) => {
      const parsedVolume = parseFloat(positionPercentage);
      if (parsedVolume <= 0) {
        setPlayerData({
          isDragToMute: true,
          isMuted: true,
          isFirstUnmuted: false,
        });
      }
      if (isMuted && parsedVolume > 0) {
        if (isDragToMute) {
          setPlayerData({ isDragToMute: false });
        }
        setPlayerData({ isMuted: false, isFirstUnmuted: false });
      }
      setPlayerDataThrottled({ volume: parsedVolume });
    },
    [setPlayerDataThrottled, isMuted, isDragToMute, setPlayerData]
  );

  return (
    <StyledVolumeController>
      <Tooltip
        // Tooltip only decides whether to auto-open on mount, so remount it
        // when the remote config arrives late or the entry gate clears.
        key={`unmute-hint-${isUnmuteHintEnabled}-${isCoveredByEntryGate}-${isStreamActivated}`}
        position="top-left"
        trigger="auto"
        disabled={!shouldTriggerUnmuteHint}
        // SwipeableViews traps the hint twice: `z-index: -1` caps the
        // subtree, and its inner `transform` re-anchors `position: fixed`.
        shouldRenderHintInModalRoot
        // Fall back to Tooltip's own default when the config is missing.
        autoCloseDelaySeconds={unmuteHintDurationSeconds || undefined}
        onHintOpened={handleUnmuteHintOpened}
        renderHint={renderUnmuteHint}
      >
        <MuteButton
          data-element_id={
            isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
          }
          onClick={handleMuteOnClick}
        >
          <img src={isMuted ? AudioOffIconSrc : AudioOnIconSrc} alt="Mute" />
        </MuteButton>
      </Tooltip>
      {isRenderSlider && (
        <VolumeSliderWrapper>
          <Seekbar
            thumbSize={12}
            seekbarHeight={2}
            value={isMuted ? 0 : volume}
            min={0}
            max={1}
            onChange={handleVolumeChange}
            onSeekStart={handleSeekStart}
            currentValueColor={color.white}
            isShowHoverFeedback
          />
        </VolumeSliderWrapper>
      )}
    </StyledVolumeController>
  );
};

VolumeController.propTypes = {
  volume: PropTypes.number,
  isMuted: PropTypes.bool,
  isRenderSlider: PropTypes.bool,
  isDragToMute: PropTypes.bool,
  volumeBeforeDragToMute: PropTypes.number,
  isStreamActivated: PropTypes.bool,
  isCoveredByEntryGate: PropTypes.bool,
  isUnmuteHintEnabled: PropTypes.bool,
  shouldDisplayUnmuteHint: PropTypes.bool,
  unmuteHintDurationSeconds: PropTypes.number,
  setPlayerData: PropTypes.func,
  markUnmuteHintSeen: PropTypes.func,
};

const StyledVolumeController = styled.div`
  min-height: 36px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  gap: 10.5px;
`;

const ControlButton = styled.button`
  ${ResetButtonStyle};
  min-width: 36px;
  min-height: 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  > img {
    object-fit: contain;
  }
`;

const MuteButton = styled(ControlButton)`
  :hover {
    background-color: ${color.neutral['100_alpha_20']};
  }
  transition: background-color 0.1s ease-in-out;
  > img {
    width: 24px;
    height: 24px;
  }
`;

const VolumeSliderWrapper = styled.div`
  position: relative;
  width: 43px;
  height: 32px;
  display: flex;
  align-items: center;
`;

export default VolumeController;
